// invest_FE/src/pages/MyInquiriesPage.js
import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { CartContext } from '../context/CartContext';
import { NotificationContext } from '../context/NotificationContext';
import { apiFetch } from '../utils/apiClient';
import { BASE_URL } from '../utils/config';
import Tabs from '../components/Tabs'; // Import the shared Tabs component
import InquiryCard from '../components/inquiries/InquiryCard';
import './MyInquiriesPage.css';

const MyInquiriesPage = () => {
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const { cart } = useContext(CartContext);
  const location = useLocation();

  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0); // Add a refresh trigger

  // Determine if we are in admin 'all' mode or user 'my' mode
  const searchParams = new URLSearchParams(location.search);
  const view = searchParams.get('view');
  const isAdminView = view === 'all' && user.isAdmin;
  const pageTitle = isAdminView ? 'All Inquiries' : 'My Inquiries';

  // Create a set of inquiry IDs that are in the cart for quick lookup.
  const inquiryIdsInCart = new Set(cart.items.filter((item) => item.productType === 'inquiry' && item.inquiryId).map((item) => item.inquiryId));

  // Get visibility days from the first inquiry, with a fallback.
  const visibilityDays = inquiries.length > 0 && inquiries[0].visibilityDays ? inquiries[0].visibilityDays : 30;

  useEffect(() => {
    const fetchInquiries = async () => {
      if (!user || !user.isLoggedIn) return;
      setIsLoading(true);

      try {
        const scope = isAdminView ? 'all' : 'my';
        const url = `${BASE_URL}/api/inquiries?status=${activeTab}&page=${page}&limit=10&scope=${scope}`;
        const response = await apiFetch(url, {}, user.token);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch inquiries.');
        }

        const data = await response.json();
        setInquiries(data.inquiries);
        setTotalPages(data.totalPages);
      } catch (error) {
        addNotification(`Error: ${error.message}`, 'error');
        setInquiries([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInquiries();
  }, [user, isAdminView, activeTab, page, addNotification, refreshKey]); // Add refreshKey to dependencies

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setPage(1); // Reset to first page when changing tabs
  };

  const handleInquiryUpdate = (newStatus) => {
    if (newStatus) {
      setActiveTab(newStatus);
    } else {
      // Otherwise, just trigger a refresh of the current tab.
      setRefreshKey((prevKey) => prevKey + 1);
    }
  };

  const TABS = ['pending', 'unlocked', 'expired', 'refused'];

  const tabsConfig = TABS.map((tab) => ({
    id: tab,
    label: tab.charAt(0).toUpperCase() + tab.slice(1),
  }));

  return (
    <div className="inquiries-page-container">
      <div className={`unlocked-inquiries-note ${activeTab === 'unlocked' && !isAdminView ? 'visible' : ''}`}>
        <p>
          <strong>Note:</strong> For your convenience, these details will remain visible here for <strong>{visibilityDays} days</strong> from the unlock date.
        </p>
      </div>

      <h1>{pageTitle}</h1>

      <Tabs tabsConfig={tabsConfig} activeTab={activeTab} onTabClick={handleTabClick} />

      <div className="inquiries-list">
        {isLoading ? (
          <p>Loading inquiries...</p>
        ) : inquiries.length > 0 ? (
          inquiries.map((inquiry) => <InquiryCard key={inquiry._id} inquiry={inquiry} onUpdate={handleInquiryUpdate} isAdminView={isAdminView} isInCart={inquiryIdsInCart.has(inquiry._id)} />)
        ) : (
          <p>No inquiries found for this category.</p>
        )}
      </div>

      {/* We will add a proper pagination component later */}
      {totalPages > 1 && (
        <div className="pagination-placeholder">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MyInquiriesPage;
